import { generateOracleSQL } from './oracle-sql.js';
import { relationalText, relationalSVG, notationText, notationHTML, notationSVG } from './relational-model.js';

const element = id => document.getElementById(id);
function download(content, name, type) {
    const url = URL.createObjectURL(content instanceof Blob ? content : new Blob([content], { type }));
    const link = document.createElement('a');
    link.href = url; link.download = name; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export class SchemaExporter {
    constructor(app) {
        this.app = app;
        this.options = { owners: Object.create(null), keys: Object.create(null) };
        for (const [id, tab] of [['btn-mld', 'mld'], ['export-mld', 'mld'], ['export-sql', 'sql']]) {
            element(id).onclick = () => { app.closeModal('export-modal'); this.open(tab); };
        }
        for (const tab of ['mld', 'sql']) element(`schema-tab-${tab}`).onclick = () => this.setTab(tab);
        element('sql-modal-close').onclick = () => app.closeModal('sql-modal');
        element('schema-copy').onclick = () => this.copy();
        element('schema-download').onclick = () => {
            if (!this.result.sql) return;
            download(this.tab === 'sql' ? this.result.sql : this.text, this.tab === 'sql' ? 'modele-oracle.sql' : 'modele-relationnel.txt', 'text/plain;charset=utf-8');
            app.showToast('Fichier exporté.');
        };
        element('schema-svg').onclick = () => { if (this.result.sql) download(this.diagram.svg, 'modele-relationnel.svg', 'image/svg+xml;charset=utf-8'); };
        element('schema-png').onclick = () => this.png();
        element('schema-format').onchange = () => this.renderPreview();
        element('schema-zoom').onchange = () => {
            const svg = element('schema-diagram').querySelector('svg');
            if (svg) svg.style.width = element('schema-zoom').value === 'fit' ? '100%' : `${this.diagram.width}px`;
        };
    }

    open(tab = 'mld') {
        this.render(); this.setTab(tab); this.app.openModal('sql-modal');
    }

    setTab(tab) {
        this.tab = tab;
        for (const key of ['mld', 'sql']) {
            element(`schema-${key}`).hidden = key !== tab;
            element(`schema-tab-${key}`).setAttribute('aria-pressed', String(key === tab));
        }
        element('schema-copy').textContent = tab === 'sql' ? 'Copier le SQL' : 'Copier avec format';
        element('schema-download').textContent = tab === 'sql' ? 'Télécharger .sql' : 'Télécharger .txt';
    }

    render() {
        const result = this.result = generateOracleSQL(this.app.state.toData(), this.options);
        const choices = element('schema-decisions'); choices.replaceChildren();
        for (const decision of result.decisions) {
            const label = document.createElement('label'); label.textContent = decision.title;
            const select = document.createElement('select'); select.dataset.decision = decision.id;
            select.add(new Option('Choisissez…', ''));
            for (const choice of decision.choices) select.add(new Option(choice.label, choice.value));
            const key = decision.kind === 'owner' ? 'owners' : 'keys';
            select.value = this.options[key][decision.id] || '';
            select.onchange = () => {
                this.options[key][decision.id] = select.value; this.render();
                element('schema-decisions').querySelector(`[data-decision="${CSS.escape(decision.id)}"]`)?.focus();
            };
            label.append(select); choices.append(label);
        }
        choices.hidden = !result.decisions.length;
        this.issues('schema-errors', result.errors);
        this.issues('schema-warnings-list', result.warnings);
        element('schema-warnings').hidden = !result.warnings.length;
        element('schema-warning-count').textContent = `${result.warnings.length} point(s) à vérifier`;
        const ready = !result.errors.length;
        element('schema-summary').textContent = ready
            ? `${result.tables.length} table(s) · ${result.tables.reduce((n, t) => n + t.foreignKeys.length, 0)} clé(s) étrangère(s) · Profil Oracle 19c+`
            : `${result.errors.length} point(s) à résoudre pour générer le modèle et le SQL.`;
        element('schema-sql-code').value = result.sql;
        this.text = ready ? notationText(result.tables) : '';
        this.html = ready ? notationHTML(result.tables) : '';
        element('schema-mld-text').value = this.text;
        element('schema-reference-text').value = ready ? relationalText(result.tables) + (result.warnings.length ? '\nPOINTS À VÉRIFIER\n' + result.warnings.map(w => `• ${w.message}`).join('\n') : '') : '';
        element('schema-diagram').replaceChildren();
        element('schema-mappings').replaceChildren();
        if (ready) {
            this.renderPreview();
            for (const relation of result.relationships) {
                const p = document.createElement('p'); p.textContent = `${relation.name} : ${relation.description}`; element('schema-mappings').append(p);
            }
        }
        for (const id of ['schema-copy', 'schema-download', 'schema-svg', 'schema-png', 'schema-zoom', 'schema-format']) element(id).disabled = !ready;
        element('schema-status').textContent = '';
    }

    renderPreview() {
        if (!this.result.sql) return;
        const context = document.createElement('canvas').getContext('2d');
        if (element('schema-format').value === 'notation') {
            this.diagram = notationSVG(this.result.tables, (text, bold) => {
                context.font = `${bold ? 700 : 400} 18px Arial,sans-serif`; return context.measureText(text).width;
            });
        } else {
            context.font = '12px monospace'; this.diagram = relationalSVG(this.result.tables, text => context.measureText(text).width);
        }
        element('schema-diagram').innerHTML = this.diagram.svg;
        element('schema-zoom').onchange();
    }

    issues(id, issues) {
        const list = element(id); list.replaceChildren(); list.hidden = !issues.length;
        for (const issue of issues) {
            const item = document.createElement('li'), text = document.createElement('span'); text.textContent = issue.message; item.append(text);
            if (issue.source && !['owner-required', 'key-required'].includes(issue.code)) {
                const button = document.createElement('button'); button.className = 'schema-edit'; button.textContent = 'Modifier';
                button.onclick = () => {
                    this.app.closeModal('sql-modal');
                    const { type, id, attributeId } = issue.source, manager = this.app.modalManager;
                    if (type === 'entity') manager.openEntityModal(id);
                    else if (type === 'association') manager.openAssociationModal(id);
                    else if (type === 'connection') manager.openConnectionModal(id);
                    if (attributeId) document.querySelector(`.modal-overlay.active [data-attribute-id="${CSS.escape(attributeId)}"] input`)?.focus();
                };
                item.append(button);
            }
            list.append(item);
        }
    }

    async copy() {
        if (!this.result.sql) return;
        const field = element(this.tab === 'sql' ? 'schema-sql-code' : 'schema-mld-text');
        try {
            try {
                if (this.tab === 'sql') await navigator.clipboard.writeText(field.value);
                else await navigator.clipboard.write([new ClipboardItem({
                    'text/plain': new Blob([this.text], { type: 'text/plain' }),
                    'text/html': new Blob([this.html], { type: 'text/html' })
                })]);
            } catch {
                field.focus(); field.select();
                const richCopy = event => {
                    event.clipboardData.setData('text/plain', this.text); event.clipboardData.setData('text/html', this.html); event.preventDefault();
                };
                if (this.tab === 'mld') document.addEventListener('copy', richCopy);
                try { if (!document.execCommand('copy')) throw new Error(); }
                finally { document.removeEventListener('copy', richCopy); }
            }
            this.app.showToast('Copié dans le presse-papiers.');
        } catch { element('schema-status').textContent = 'La copie automatique a échoué. Sélectionnez le texte ou téléchargez le fichier.'; }
    }

    async png() {
        if (!this.result.sql) return;
        const diagram = this.diagram;
        const ratio = Math.min(2, 8192 / diagram.width, 8192 / diagram.height, Math.sqrt(16000000 / (diagram.width * diagram.height)));
        const url = URL.createObjectURL(new Blob([diagram.svg], { type: 'image/svg+xml;charset=utf-8' }));
        element('schema-png').disabled = true;
        try {
            const image = new Image(); image.src = url; await image.decode();
            const canvas = document.createElement('canvas'); canvas.width = Math.max(1, Math.floor(diagram.width * ratio)); canvas.height = Math.max(1, Math.floor(diagram.height * ratio));
            canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            if (!blob) throw new Error(); download(blob, 'modele-relationnel.png');
            this.app.showToast('Image du modèle relationnel exportée.');
        } catch { element('schema-status').textContent = 'Impossible de créer le PNG. Téléchargez le SVG pour conserver toute la définition.'; }
        finally { URL.revokeObjectURL(url); element('schema-png').disabled = !this.result.sql; }
    }
}
